import BpkBadge from '@skyscanner-internal/backpack-web/bpk-component-badge';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  constants,
  namespace,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import classnames from 'classnames';
import reactReplacePlaceholder from 'react-replace-placeholder';
import { GLOBAL } from 'saddlebag-browser';
import observer from 'saddlebag-observer';

import DesktopPriceSection from '../DesktopPriceSection';
import MobilePriceSection from '../MobilePriceSection';

import type { NearbyAirportsStrings } from '@skyscanner-internal/landing-pages-shared-types/types-shared/NearbyAirportsProps';
import type { TrackingContextProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/TrackingContextProps';

import STYLES from './AirportCard.module.scss';

export type AirportCardProps = {
  airportName: string;
  destinationName: string;
  distance: number;
  distanceUnit: string;
  formattedPrice: string | null;
  index: number;
  isCheapest?: boolean;
  isClosest?: boolean;
  isMatchingAirline?: boolean;
  carrierName?: string;
  isMobile: boolean;
  isReturn: boolean;
  link: string;
  strings: NearbyAirportsStrings;
  trackingContext?: TrackingContextProps;
};

const extendedOnClick = (
  componentName: string,
  link: string,
  { currency, deviceType, locale, market, pageType }: TrackingContextProps,
) => {
  observer.publish('grappler-track', {
    eventName: 'NearbyAirportsClicked',
    fullSchemaName: 'nearby_airports.NearbyAirportsClicked',
    message: {
      common_properties: {
        culture_settings: {
          country: market,
          currency,
          locale,
        },
        page_type: constants[pageType],
        platform: deviceType || constants.UNSET_PLATFORM,
      },
      component_clicked: componentName,
    },
    isMiniEvent: true,
    consentRequired: true,
    microsite: {
      name: namespace,
    },
  });

  const globalWindow = GLOBAL.getWindow();
  globalWindow.setTimeout(() => {
    globalWindow.location.href = link;
  }, 500);
};

// Narrowed type for the variable makes Typescript happy when
// we use it as an index on the strings object.
type DistanceUnitStringKey =
  | 'component_airports-distance-km'
  | 'component_airports-distance-mile'
  | 'component_airports-distance-miles';

const getDistanceString = ({
  destinationName,
  distance,
  distanceUnit,
  strings,
}: {
  destinationName: string;
  distance: number;
  distanceUnit: string;
  strings: NearbyAirportsStrings;
}) => {
  let distanceUnitStringKey: DistanceUnitStringKey =
    'component_airports-distance-km';
  if (distanceUnit === 'MILE') {
    if (distance === 1) {
      distanceUnitStringKey = 'component_airports-distance-mile';
    } else {
      distanceUnitStringKey = 'component_airports-distance-miles';
    }
  }
  const distanceUnitString = strings[distanceUnitStringKey];
  return reactReplacePlaceholder(
    distanceUnitString,
    { distance, city: destinationName },
    '{',
    '}',
  );
};

const getAriaLabel = ({
  carrierName,
  destinationName,
  formattedPrice,
  isCheapest,
  isClosest,
  isMatchingAirline,
  isReturn,
  strings,
}: {
  carrierName?: string;
  destinationName: string;
  formattedPrice: string | null;
  isCheapest: boolean;
  isClosest: boolean;
  isMatchingAirline: boolean;
  isReturn: boolean;
  strings: NearbyAirportsStrings;
}): string => {
  let airportStringArray: Array<string | JSX.Element> = [];
  if (isCheapest && isClosest) {
    airportStringArray = [
      strings['component_airports_screenReader-closest-and-cheapest-airport'],
    ];
  } else if (isCheapest) {
    airportStringArray = [
      strings['component_city-airline-screenReader-cheapest'],
    ];
  } else if (isClosest) {
    airportStringArray = reactReplacePlaceholder(
      strings['component_city-airline-screenReader-closest'],
      { city: destinationName },
      '@@',
      '@@',
    );
  } else {
    airportStringArray = [strings['component_airports_screenReader-airport']];
  }

  if (isMatchingAirline) {
    airportStringArray = [
      ...airportStringArray,
      ...reactReplacePlaceholder(
        strings['component_city-airline-available'],
        { airline: carrierName },
        '@@',
        '@@',
      ),
    ];
  }

  if (formattedPrice) {
    const priceString = isReturn
      ? ` ${strings['component_city-airline-screenReader-price']}`
      : ` ${strings['component_airports_screenReader-flights-from-price']}`;

    airportStringArray.push(
      reactReplacePlaceholder(
        priceString,
        { price: formattedPrice },
        '@@',
        '@@',
      ).join(''),
    );
  }

  airportStringArray.push(` ${strings['component_airports-search-flights']}`);

  return airportStringArray.join('');
};

const AirportCard = ({
  airportName,
  carrierName = undefined,
  destinationName,
  distance,
  distanceUnit,
  formattedPrice,
  index,
  isCheapest = false,
  isClosest = false,
  isMatchingAirline = false,
  isMobile,
  isReturn,
  link,
  strings,
  trackingContext = undefined,
}: AirportCardProps) => {
  const badges = [];

  if (isMatchingAirline) {
    badges.push(
      <BpkBadge
        className={classnames(STYLES.label, STYLES.labelSuccess)}
        key="matching"
      >
        {reactReplacePlaceholder(
          strings['component_city-airline-available'],
          { airline: carrierName },
          '@@',
          '@@',
        )}
      </BpkBadge>,
    );
  }
  if (isClosest) {
    badges.push(
      <BpkBadge className={STYLES.label} key="closest">
        {strings['component_airports-closest']}
      </BpkBadge>,
    );
  }
  if (isCheapest) {
    badges.push(
      <BpkBadge className={STYLES.label} key="cheapest">
        {strings['component_airports-cheapest']}
      </BpkBadge>,
    );
  }

  const distanceString = getDistanceString({
    destinationName,
    distance,
    distanceUnit,
    strings,
  }).join('');
  const ariaLabelArray = reactReplacePlaceholder(
    getAriaLabel({
      carrierName,
      destinationName,
      formattedPrice,
      isCheapest,
      isClosest,
      isMatchingAirline,
      isReturn,
      strings,
    }),
    {
      airport: airportName,
      distanceString,
      price: formattedPrice,
    },
    '{',
    '}',
  );
  const ariaLabelString = ariaLabelArray.join('');

  return (
    <div className={STYLES.AirportCard}>
      <BpkCard
        key={`${airportName}-${index + 1}`}
        className={STYLES.card}
        padded={false}
        aria-label={ariaLabelString}
        href={link}
        rel="nofollow"
        onClick={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
          trackingContext &&
            extendedOnClick('AIRPORT_CARD', link, trackingContext);
        }}
      >
        <div className={STYLES.infoContainer}>
          <BpkText
            className={STYLES.title}
            textStyle={TEXT_STYLES.label1}
            tagName="h3"
          >
            {airportName}
          </BpkText>
          <BpkText
            color={TEXT_COLORS.textSecondary}
            className={STYLES.miles}
            tagName="text"
            textStyle={TEXT_STYLES.sm}
          >
            {distanceString}
          </BpkText>
          {badges.length > 0 ? (
            <div className={STYLES.badgesContainer}>{badges}</div>
          ) : null}
        </div>
        {isMobile ? (
          <MobilePriceSection {...{ formattedPrice, strings }} />
        ) : (
          <DesktopPriceSection {...{ formattedPrice, strings }} />
        )}
      </BpkCard>
    </div>
  );
};

export default AirportCard;
